/**
 * Person colour-coding (issue #615).
 *
 * The roster heatmap's **stations-view** lists one row per (station,
 * session); a cell holds whoever is assigned there that day. Colouring
 * those cells per *station* (the members-view scheme) is useless in
 * stations-view — a row is already one station, so the whole row is one
 * colour. Colouring per *person* instead lets a member scan a column /
 * the whole grid and spot their own allocations across stations.
 *
 * This mirrors `stationColors.ts`'s deterministic auto-assign exactly:
 * a stable hash of a person key indexes into the **same** fixed accessible
 * palette (`STATION_COLOR_SPECS`). We deliberately reuse that palette
 * rather than introduce a second one — one accessible, theme-independent
 * set of hues, two hashing entry points. There is no per-person override
 * UI; person colour is always auto-assigned.
 *
 * The "person key" is the member display name — the same stable string
 * the stations-view cell already carries, and the same by-name approach
 * `stationColors.ts` uses for stations. Deterministic across renders,
 * cycles, and sessions.
 */

import { STATION_COLOR_SPECS, STATION_COLOR_TOKENS, type StationColorToken } from "./stationColors";

/**
 * Stable string hash (FNV-1a, 32-bit). Identical algorithm to
 * `stationColors.hashStationName` — kept as a local copy because that
 * one is module-private. Deterministic; not cryptographic; it only needs
 * an even-ish spread over the ~10-slot palette.
 */
function hashPersonKey(key: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i);
    // 32-bit FNV prime multiply, kept in uint32 range.
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

/**
 * Auto-assign a palette token to a person purely from a stable key
 * (their display name). Deterministic — same key in → same token out.
 */
export function autoPersonColorToken(personKey: string): StationColorToken {
  const idx = hashPersonKey(personKey) % STATION_COLOR_TOKENS.length;
  return STATION_COLOR_TOKENS[idx] as StationColorToken;
}

/**
 * Resolve a person straight to their colour spec (class strings + raw
 * colours) from the shared accessible palette. Convenience over
 * `autoPersonColorToken` + a `STATION_COLOR_SPECS` lookup at the call
 * site.
 */
export function personColor(personKey: string) {
  return STATION_COLOR_SPECS[autoPersonColorToken(personKey)];
}
